/**
 * A CONTA DE VALIDAÇÃO — um acesso que mede o produto autenticado sem alcançar criança nenhuma.
 *
 * *"crie um acesso que só você como a gente possa ter para validar. procure um padrão de projeto
 * que permita isso."*
 *
 * ── O PROBLEMA, E ELE NÃO É "COMO FAZER LOGIN" ──────────────────────────────────────────────
 *
 * Metade deste produto fica atrás da porta: o editor, o acervo e o publicar. Nenhuma rodada os
 * mediu desde 24/09, e dezoito nós do épico estão parados por isso. A pergunta óbvia é como um
 * agente entra. A pergunta certa é outra: **o que ele pode alcançar depois de entrar.**
 *
 * Esta plataforma é usada por crianças de 6 anos e tem uma biblioteca pública. Um acesso de
 * validação mal contido faz duas coisas que ninguém quer:
 *
 *   1. os quadrinhos de teste aparecem na biblioteca que a escola vê — e isso NÃO é hipótese:
 *      o `PL-T12.11` existe porque hoje a biblioteca mostra *"só uma história de teste"*;
 *   2. uma credencial de longa vida passa a existir contra produção.
 *
 * Então o padrão não é "uma conta com senha no CI". É uma **identidade contida**, e a contenção
 * mora no motor de regras, não na tela.
 *
 * ── O PADRÃO, EM TRÊS PEÇAS ─────────────────────────────────────────────────────────────────
 *
 * **1 · A marca não é um campo da conta.** O `firestore.rules` deste produto já diz, e vale aqui
 * em dobro: *"o TIPO DE CONTA declarado em `users/{uid}` NÃO serve de autorização aqui. O cliente
 * escreve aquele campo, e quem escreve pode mentir."* Se a marca fosse um campo do próprio
 * cadastro, a conta de validação a apagaria e publicaria na biblioteca das crianças.
 *
 * Então a marca é a EXISTÊNCIA de `contas-de-validacao/{uid}`, coleção com `allow write: if
 * false` — ninguém a escreve pelo cliente. É a mesma forma de `administradores`, pelo mesmo
 * motivo, e entra pelo mesmo caminho: o console, pela equipe.
 *
 * **2 · A contenção reusa o portão que já existe.** Uma conta de validação publica com o estado
 * `publicado-validacao`, e não com `publicado`. Isso não pede consulta nova nem índice novo:
 *
 *     a biblioteca consulta  `where("publicacao.estado", "==", "publicado")`   → não traz
 *     a abertura consulta    `where("publicacao.estado", "==", "publicado")`   → não traz
 *     a leitura exige        `ficha.publicacao.estado == 'publicado'`          → recusa
 *
 * Três portas que já estavam fechadas para rascunho fecham sozinhas para validação. **Nada do que
 * a conta de validação publicar alcança a tela de uma criança** — e o caminho de publicar é
 * exercitado inteiro, que é o que precisa ser medido.
 *
 * E a regra trava os dois lados: quem está na coleção **só** pode usar `publicado-validacao`;
 * quem não está **nunca** pode usá-lo. Uma conta de validação não consegue se disfarçar de conta
 * comum, e uma conta comum não consegue sumir da biblioteca fingindo ser de validação.
 *
 * **3 · O que chega ao agente é a SESSÃO, nunca o segredo.** Um processo separado lê a senha do
 * ambiente, faz o login pela tela de verdade e guarda **só o estado da sessão**. O agente dirige
 * a sessão. A senha não passa por conversa, não entra em commit e não aparece em registro nenhum.
 * O caminho está em `rastro/validacao/` do ticket.
 *
 * ── O QUE ESTE PADRÃO NÃO DÁ, E ESTÁ DITO ───────────────────────────────────────────────────
 *
 * Ele não dá acesso a dado de ninguém. As regras já prendem cada pessoa ao próprio acervo, e a
 * conta de validação não é exceção: ela lê e escreve o que ela mesma criou, e mais nada. Ela
 * **não** entra em `administradores` — uma identidade automática que pudesse mexer nas regras de
 * conteúdo de uma plataforma infantil é exatamente o que o T17 construiu para impedir.
 *
 * E ela é revogável num gesto: apagar `contas-de-validacao/{uid}` tira a marca, apagar a conta
 * tira o acesso. Nenhum dos dois pede deploy.
 */

import Firebase, { auth } from "../api/firebase.class";

/** Onde a marca mora. Uma coleção que o cliente lê sobre si e não escreve nunca. */
export const COLECAO_DAS_CONTAS_DE_VALIDACAO = "contas-de-validacao";

/**
 * O estado de publicação de uma conta de validação.
 *
 * Ele existe para NÃO ser `"publicado"`. Toda consulta e toda regra que já filtravam por
 * `"publicado"` passam a conter a validação sem uma linha de mudança.
 */
export const PUBLICADO_DE_VALIDACAO = "publicado-validacao";

/**
 * A pessoa logada é uma conta de validação?
 *
 * `false` para quem não está logada, para quem não está na coleção, e **para qualquer erro de
 * leitura**. Erro vira "não", nunca "sim" — e aqui a direção segura é contraintuitiva: um "sim"
 * errado esconderia da biblioteca o quadrinho de uma criança de verdade.
 */
export async function souContaDeValidacao(): Promise<boolean> {
  const uid = auth?.currentUser?.uid;
  if (!uid) return false;
  try {
    const doc = await Firebase.getDocument(COLECAO_DAS_CONTAS_DE_VALIDACAO, uid);
    return !!doc;
  } catch {
    return false;
  }
}

/**
 * O estado que esta conta deve gravar ao publicar.
 *
 * `ehValidacao` vem de `souContaDeValidacao()`. A função é pura para que o teste possa cobrar as
 * quatro combinações sem subir Firebase nenhum — e porque a decisão de conter não pode depender
 * de uma chamada de rede ter dado certo.
 */
export function estadoAoPublicar(publicado: boolean, ehValidacao: boolean): string {
  if (!publicado) return "rascunho";
  return ehValidacao ? PUBLICADO_DE_VALIDACAO : "publicado";
}

/**
 * Este documento alcança a biblioteca pública?
 *
 * Existe para o teste poder perguntar isso de um documento qualquer, inclusive de um montado à
 * mão com um estado inventado. Só uma string passa, e é a que as três consultas usam.
 */
export function alcancaABibliotecaPublica(estado: unknown): boolean {
  return estado === "publicado";
}
